import React from 'react';
import LessonIcon from './LessonIcon';

// One bright color family per lesson section, cycled in section order.
export const LESSON_THEMES = [
  { tile: 'from-pink-400 to-rose-500', title: 'from-pink-500 to-rose-600', card: 'from-pink-50 to-rose-100', icon: 'text-rose-500', border: 'border-rose-200 hover:border-rose-400', num: 'bg-rose-500' },
  { tile: 'from-sky-400 to-blue-500', title: 'from-sky-500 to-blue-600', card: 'from-sky-50 to-blue-100', icon: 'text-blue-500', border: 'border-blue-200 hover:border-blue-400', num: 'bg-blue-500' },
  { tile: 'from-amber-300 to-orange-500', title: 'from-amber-500 to-orange-600', card: 'from-amber-50 to-orange-100', icon: 'text-orange-500', border: 'border-orange-200 hover:border-orange-400', num: 'bg-orange-500' },
  { tile: 'from-emerald-400 to-teal-500', title: 'from-emerald-500 to-teal-600', card: 'from-emerald-50 to-teal-100', icon: 'text-teal-600', border: 'border-teal-200 hover:border-teal-400', num: 'bg-teal-500' },
  { tile: 'from-violet-400 to-purple-500', title: 'from-violet-500 to-purple-600', card: 'from-violet-50 to-purple-100', icon: 'text-purple-500', border: 'border-purple-200 hover:border-purple-400', num: 'bg-purple-500' },
  { tile: 'from-fuchsia-400 to-pink-500', title: 'from-fuchsia-500 to-pink-600', card: 'from-fuchsia-50 to-pink-100', icon: 'text-fuchsia-500', border: 'border-fuchsia-200 hover:border-fuchsia-400', num: 'bg-fuchsia-500' },
  { tile: 'from-lime-400 to-green-500', title: 'from-lime-600 to-green-600', card: 'from-lime-50 to-green-100', icon: 'text-green-600', border: 'border-green-200 hover:border-green-400', num: 'bg-green-500' },
  { tile: 'from-cyan-400 to-sky-500', title: 'from-cyan-500 to-sky-600', card: 'from-cyan-50 to-sky-100', icon: 'text-cyan-600', border: 'border-cyan-200 hover:border-cyan-400', num: 'bg-cyan-500' },
];

export const lessonTheme = (index: number) => LESSON_THEMES[((index % LESSON_THEMES.length) + LESSON_THEMES.length) % LESSON_THEMES.length];

interface LessonCardProps {
  lessonNumber?: number;
  title: string;
  status: 'locked' | 'unlocked' | 'completed';
  onClick: () => void;
  isFirst?: boolean;
  isNext?: boolean; // the lesson the student should do next -- glows and says so
  theme?: number;
}

const LessonCard: React.FC<LessonCardProps> = ({ lessonNumber, title, status, onClick, isFirst = false, isNext = false, theme = 1 }) => {
  const isDisabled = status === 'locked';
  const t = lessonTheme(theme);

  const getIconType = (title: string): string => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('juego') || lowerTitle.includes('game')) return 'game';
    if (lowerTitle.includes('práctica') || lowerTitle.includes('practice')) return 'practice';
    if (lowerTitle.includes('revisión') || lowerTitle.includes('review')) return 'review';
    if (lowerTitle.includes('introducción')) return 'introduction';
    if (lowerTitle.includes('fila guía') || lowerTitle.includes('home row')) return 'home_row';
    const keyMatch = lowerTitle.match(/teclas ([a-z]\s?&\s?[a-z])/); // e.g., "Teclas f & j"
    if (keyMatch && keyMatch[1]) return keyMatch[1].replace(/\s/g, '');
    return 'default';
  };

  const iconType = getIconType(title);

  return (
    <div className="relative">
      <button
        onClick={onClick}
        disabled={isDisabled}
        className={`group fun-card w-full h-40 p-3 border-2 rounded-2xl shadow-md flex flex-col justify-between items-center text-center transition-all duration-200
          ${isDisabled
            ? `opacity-50 saturate-50 cursor-not-allowed bg-gradient-to-br ${t.card} border-white`
            : `bg-gradient-to-br ${t.card} ${t.border} hover:shadow-xl hover:-translate-y-1 hover:scale-105`
          }
          ${isNext ? 'fun-glow ring-4 ring-yellow-300' : ''}
        `}
        aria-label={`${title}${lessonNumber ? `, Lesson ${lessonNumber}` : ''}, Status: ${status}`}
      >
        <div className="w-full flex justify-between items-start text-gray-400">
          {lessonNumber ? (
            <span className={`min-w-[1.75rem] h-7 px-1.5 rounded-full flex items-center justify-center text-sm font-black ${t.num} text-white ${isDisabled ? '' : 'shadow'}`}>{lessonNumber}</span>
          ) : (
            <span /> // Placeholder to keep icon on the right
          )}
          {status === 'locked' && (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
          )}
          {status === 'completed' && (
            <span className="text-2xl leading-none drop-shadow-[0_2px_4px_rgba(250,204,21,0.8)]" aria-hidden="true">⭐</span>
          )}
        </div>
        <div className={`w-16 h-16 ${t.icon} ${isDisabled ? '' : 'fun-wiggle-hover'}`}>
          <LessonIcon type={iconType} />
        </div>
        <span className={`text-xs font-bold leading-tight ${status === 'completed' ? 'text-green-700' : 'text-gray-700'}`}>{title}</span>
      </button>

      {isNext && !isFirst && (
        <span className="fun-bounce absolute -top-3 left-1/2 -translate-x-1/2 z-10 whitespace-nowrap px-3 py-0.5 rounded-full bg-gradient-to-r from-yellow-300 to-orange-400 text-white text-xs font-black shadow-md ring-2 ring-white pointer-events-none">
          ▶ Next!
        </span>
      )}

      {isFirst && (
        <div className="absolute top-1/2 -right-2 transform translate-x-full -translate-y-1/2 z-10 hidden lg:block" style={{width: '200px'}}>
            <div className="bg-blue-600 text-white text-sm font-bold rounded-lg p-3 shadow-lg relative">
                <p>Haz clic aquí para empezar</p>
                <div className="absolute left-0 top-1/2 -translate-x-full -translate-y-1/2 w-4 h-4">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16 8L0 0V16L16 8Z" fill="#2563eb"/>
                    </svg>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default LessonCard;
